import {
  computed,
  Injectable,
  resource,
  ResourceRef,
  Signal,
} from '@angular/core';
import {
  EConfigurationChannel,
  ELicenseChannelState,
  ILicense,
  LicenseKey,
} from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { firstValueFrom } from 'rxjs';
import { LicenseGateway } from './license.gateway';

@Injectable({
  providedIn: 'root',
})
export class LicenseStore {
  private readonly gateway: LicenseGateway = new LicenseGateway();

  readonly #licenseResource: ResourceRef<IApiResponse<ILicense[]> | undefined> =
    resource({
      loader: (): Promise<IApiResponse<ILicense[]>> => {
        return firstValueFrom(this.gateway.get());
      },
    });

  readonly isLoading: Signal<boolean> = computed((): boolean =>
    this.#licenseResource.isLoading()
  );

  readonly licenses: Signal<ILicense[]> = computed((): ILicense[] => {
    return this.#licenseResource.hasValue()
      ? (this.#licenseResource.value()?.data ?? [])
      : [];
  });

  /** At least one license must be active; an empty list grants no access. */
  hasAnyActiveLicense(
    keys: readonly LicenseKey[],
    channel?: EConfigurationChannel
  ): boolean {
    return keys.some(key => {
      switch (channel) {
        case EConfigurationChannel.Web:
          return this.isWebLicenseActive(key);
        case EConfigurationChannel.App:
          return this.isAppLicenseActive(key);
        default:
          return this.isWebLicenseActive(key) || this.isAppLicenseActive(key);
      }
    });
  }

  isWebLicenseActive(licenseKey: LicenseKey): boolean {
    const license: ILicense | undefined = this.licenses().find(
      (candidate: ILicense): boolean => candidate.key === licenseKey
    );

    return license?.webState === ELicenseChannelState.Active;
  }
  isAppLicenseActive(licenseKey: LicenseKey): boolean {
    const license: ILicense | undefined = this.licenses().find(
      (candidate: ILicense): boolean => candidate.key === licenseKey
    );

    return license?.appState === ELicenseChannelState.Active;
  }
  isAnyWebLicenseActive(): boolean {
    return this.licenses().some(
      (license: ILicense): boolean =>
        license.webState === ELicenseChannelState.Active
    );
  }
  isAnyAppLicenseActive(): boolean {
    return this.licenses().some(
      (license: ILicense): boolean =>
        license.appState === ELicenseChannelState.Active
    );
  }
  isAnyWebAndAppLicenseActive(): boolean {
    return this.isAnyAppLicenseActive() && this.isAnyWebLicenseActive();
  }

  reload(): void {
    this.#licenseResource.reload();
  }

  updateLicenses(licenses: ILicense[]): void {
    this.#licenseResource.update(
      (): IApiResponse<ILicense[]> => ({
        data: licenses,
      })
    );
  }

  async save(licenses: ILicense[]): Promise<void> {
    const result: IApiResponse<ILicense[]> = await firstValueFrom(
      this.gateway.save(licenses)
    );

    if (result.data) {
      this.updateLicenses(result.data);
    }
  }
}
